/**
 * Reusable "Import a serology report" flow (toxoplasmosis IgG / IgM / IgA / PCR).
 *
 * Pipeline: file picker / drag-and-drop → POST /attachments (kind
 * 'serology_report', multipart) → POST /serology/extract → editable review table
 * pre-filled from the (DRAFT) AI suggestions → POST /serology/results/import
 * (clinician-signed; one row per marker; the report is linked to the result).
 *
 * Serology is clinician-entered, so this lives on the clinician Serology screen.
 * The AI output is ALWAYS a draft the clinician verifies before saving.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SEROLOGY_MARKERS, SEROLOGY_QUALITATIVE, type SerologyMarker } from '@toxofollow/shared';
import { api, getCsrfToken, ApiError } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Button, Field, inputClass, Table } from './ui.js';
import { Select, ErrorState } from './form.js';

const L = {
  pick: { fr: 'Choisir un fichier (PDF ou photo)', en: 'Choose a file (PDF or photo)' },
  drop: { fr: 'Glissez-déposez un compte rendu de sérologie', en: 'Drag & drop a serology report' },
  dropOr: { fr: 'ou', en: 'or' },
  dropActive: { fr: 'Déposez pour importer', en: 'Drop to import' },
  unsupportedFile: { fr: 'Type de fichier non pris en charge (PDF ou image).', en: 'Unsupported file type (PDF or image).' },
  extracting: { fr: 'Analyse du document…', en: 'Reading the document…' },
  draftNotice: { fr: 'Vérifiez les valeurs extraites', en: 'Verify the extracted values' },
  draftHint: {
    fr: 'Les valeurs ci-dessous sont un brouillon issu du document (IA). Corrigez-les avant d’enregistrer.',
    en: 'The values below are a draft from the document (AI). Correct them before saving.',
  },
  manualEntry: { fr: 'Saisie manuelle', en: 'Manual entry' },
  manualHint: {
    fr: 'Aucune valeur n’a pu être extraite. Saisissez les résultats manuellement.',
    en: 'No value could be extracted. Enter the results manually.',
  },
  sampleDate: { fr: 'Date de prélèvement', en: 'Sample date' },
  resultDate: { fr: 'Date du résultat', en: 'Result date' },
  marker: { fr: 'Marqueur', en: 'Marker' },
  qualitative: { fr: 'Qualitatif', en: 'Qualitative' },
  titer: { fr: 'Titre', en: 'Titer' },
  titerUnit: { fr: 'Unité', en: 'Unit' },
  interpretation: { fr: 'Interprétation / avidité', en: 'Interpretation / avidity' },
  addRow: { fr: '+ Ajouter un marqueur', en: '+ Add marker' },
  remove: { fr: 'Retirer', en: 'Remove' },
  submit: { fr: 'Importer', en: 'Import' },
  reset: { fr: 'Recommencer', en: 'Start over' },
  saved: { fr: 'Sérologie enregistrée', en: 'Serology saved' },
  importAnother: { fr: 'Importer un autre', en: 'Import another' },
  none: { fr: '(aucun)', en: '(none)' },
} as const;

interface SerologySuggestion {
  marker: SerologyMarker;
  qualitative?: string;
  titer?: string;
  titerUnit?: string;
  interpretation?: string;
  confidence: 'low';
}
interface ExtractResponse {
  extracted: boolean;
  results: SerologySuggestion[];
  sampleDateGuess?: string;
}

interface MarkerRow {
  marker: SerologyMarker;
  qualitative: string;
  titer: string;
  titerUnit: string;
  interpretation: string;
}

// Core markers the review form always starts with (PCR appended if extracted).
const PRESET: SerologyMarker[] = ['igg', 'igm', 'iga'];
const today = () => new Date().toISOString().slice(0, 10);

function buildRows(results: SerologySuggestion[]): MarkerRow[] {
  const byMarker = new Map<string, SerologySuggestion>();
  for (const s of results) if (!byMarker.has(s.marker)) byMarker.set(s.marker, s);
  const rows: MarkerRow[] = PRESET.map((m) => {
    const hit = byMarker.get(m);
    return {
      marker: m,
      qualitative: hit?.qualitative ?? '',
      titer: hit?.titer ?? '',
      titerUnit: hit?.titerUnit ?? '',
      interpretation: hit?.interpretation ?? '',
    };
  });
  for (const s of results) {
    if ((PRESET as string[]).includes(s.marker)) continue;
    rows.push({
      marker: s.marker,
      qualitative: s.qualitative ?? '',
      titer: s.titer ?? '',
      titerUnit: s.titerUnit ?? '',
      interpretation: s.interpretation ?? '',
    });
  }
  return rows;
}

export function SerologyImport({ onDone, className }: { onDone?: () => void; className?: string }) {
  const { t, locale } = useI18n();
  const lang = locale === 'en' ? 'en' : 'fr';
  const tr = (m: { fr: string; en: string }) => m[lang];

  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [extracted, setExtracted] = useState(false);
  const [rows, setRows] = useState<MarkerRow[]>([]);
  const [sampleDate, setSampleDate] = useState(today());
  const [resultDate, setResultDate] = useState('');
  const [doneMessage, setDoneMessage] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);

  function resetAll() {
    setAttachmentId(null);
    setExtracted(false);
    setRows([]);
    setSampleDate(today());
    setResultDate('');
    setDoneMessage(null);
    setDragging(false);
    setDropError(null);
    uploadM.reset();
    importM.reset();
  }
  function setRow(i: number, patch: Partial<MarkerRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, { marker: 'igg', qualitative: '', titer: '', titerUnit: '', interpretation: '' }]);
  }
  function removeRow(i: number) {
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  }

  const uploadM = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'serology_report');
      const res = await fetch('/api/attachments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf-token': getCsrfToken() ?? '' },
        body: fd,
      });
      const text = await res.text();
      const json = text ? JSON.parse(text) : undefined;
      if (!res.ok) {
        const err = json?.error ?? {};
        throw new ApiError(res.status, err.code ?? 'upload_failed', err.message ?? res.statusText, err.details);
      }
      const id: string = json.attachment.id;
      const ex = await api.post<ExtractResponse>('/serology/extract', { attachmentId: id });
      return { id, ex };
    },
    onSuccess: ({ id, ex }) => {
      setAttachmentId(id);
      setExtracted(ex.extracted && ex.results.length > 0);
      setRows(buildRows(ex.results));
      if (ex.sampleDateGuess) setSampleDate(ex.sampleDateGuess);
    },
  });

  const importM = useMutation({
    mutationFn: () =>
      api.post('/serology/results/import', {
        sampleDate,
        resultDate: resultDate || undefined,
        sourceAttachmentId: attachmentId ?? undefined,
        results: rows
          .filter((r) => r.qualitative.trim() !== '' || r.titer.trim() !== '')
          .map((r) => ({
            marker: r.marker,
            qualitative: r.qualitative || undefined,
            titer: r.titer.trim() || undefined,
            titerUnit: r.titerUnit.trim() || undefined,
            interpretation: r.interpretation.trim() || undefined,
          })),
      }),
    onSuccess: () => {
      setDoneMessage(tr(L.saved));
      onDone?.();
    },
  });

  function acceptFile(file: File | undefined | null) {
    if (!file) return;
    const ok = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!ok) {
      setDropError(tr(L.unsupportedFile));
      return;
    }
    setDropError(null);
    uploadM.mutate(file);
  }
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = '';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!busy) acceptFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  };

  const hasForm = attachmentId !== null && doneMessage === null;
  const busy = uploadM.isPending;
  const canSubmit = rows.some((r) => r.qualitative.trim() !== '' || r.titer.trim() !== '');

  return (
    <div className={className}>
      {doneMessage !== null && (
        <div className="space-y-3">
          <div className="text-sm rounded-lg px-3 py-2 bg-green-50 border border-green-200 text-green-800" role="status">
            ✓ {doneMessage}
          </div>
          <Button type="button" variant="secondary" onClick={resetAll}>
            {tr(L.importAnother)}
          </Button>
        </div>
      )}

      {attachmentId === null && doneMessage === null && (
        <div className="space-y-2">
          <label
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition ${
              dragging ? 'border-brand bg-brand-light/40' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
            } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <span className="text-2xl" aria-hidden>🧪</span>
            <span className="text-sm font-medium text-slate-700">{dragging ? tr(L.dropActive) : tr(L.drop)}</span>
            <span className="text-xs text-slate-400">{tr(L.dropOr)}</span>
            <span className="text-sm text-brand underline">{tr(L.pick)}</span>
            <input type="file" accept="application/pdf,image/*" onChange={onFile} disabled={busy} className="hidden" />
          </label>
          {busy && <p className="text-sm text-slate-400">{tr(L.extracting)}</p>}
          {dropError && <p className="text-sm text-danger">{dropError}</p>}
          {uploadM.isError && <ErrorState error={uploadM.error} fallback={t('common.error')} />}
        </div>
      )}

      {hasForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            importM.mutate();
          }}
          className="space-y-3"
        >
          {extracted ? (
            <div className="text-sm rounded-lg px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800">
              <strong>{tr(L.draftNotice)}</strong>
              <span className="block text-amber-700">{tr(L.draftHint)}</span>
            </div>
          ) : (
            <div className="text-sm rounded-lg px-3 py-2 bg-slate-50 border border-slate-200 text-slate-700">
              <strong>{tr(L.manualEntry)}</strong>
              <span className="block text-slate-500">{tr(L.manualHint)}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label={tr(L.sampleDate)}>
              <input className={inputClass} type="date" value={sampleDate} onChange={(e) => setSampleDate(e.target.value)} required />
            </Field>
            <Field label={tr(L.resultDate)}>
              <input className={inputClass} type="date" value={resultDate} onChange={(e) => setResultDate(e.target.value)} />
            </Field>
          </div>

          <Table headers={[tr(L.marker), tr(L.qualitative), tr(L.titer), tr(L.titerUnit), tr(L.interpretation), '']}>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1 pr-2 min-w-24">
                  <Select value={r.marker} onChange={(e) => setRow(i, { marker: e.target.value as SerologyMarker })}>
                    {SEROLOGY_MARKERS.map((m) => (
                      <option key={m} value={m}>{t(`serology.${m}`)}</option>
                    ))}
                  </Select>
                </td>
                <td className="py-1 pr-2 min-w-28">
                  <Select value={r.qualitative} onChange={(e) => setRow(i, { qualitative: e.target.value })}>
                    <option value="">{tr(L.none)}</option>
                    {SEROLOGY_QUALITATIVE.map((qv) => (
                      <option key={qv} value={qv}>{qv}</option>
                    ))}
                  </Select>
                </td>
                <td className="py-1 pr-2">
                  <input className={inputClass} inputMode="decimal" value={r.titer} onChange={(e) => setRow(i, { titer: e.target.value })} />
                </td>
                <td className="py-1 pr-2 min-w-20">
                  <input className={inputClass} value={r.titerUnit} onChange={(e) => setRow(i, { titerUnit: e.target.value })} placeholder="UI/mL" />
                </td>
                <td className="py-1 pr-2">
                  <input className={inputClass} value={r.interpretation} onChange={(e) => setRow(i, { interpretation: e.target.value })} />
                </td>
                <td className="py-1 pr-2 whitespace-nowrap">
                  <button type="button" className="text-xs underline text-danger" onClick={() => removeRow(i)}>
                    {tr(L.remove)}
                  </button>
                </td>
              </tr>
            ))}
          </Table>

          <button type="button" className="text-sm underline text-brand" onClick={addRow}>
            {tr(L.addRow)}
          </button>

          {importM.isError && <ErrorState error={importM.error} fallback={t('common.error')} />}

          <div className="flex gap-2">
            <Button type="submit" disabled={importM.isPending || !canSubmit}>
              {tr(L.submit)}
            </Button>
            <Button type="button" variant="secondary" onClick={resetAll}>
              {tr(L.reset)}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export { L as serologyImportStrings };
